import React from "react";
import type {
  BlastFrameMatchedCatalogue,
  EquipmentKind,
  InductionCoilMatchedCatalogue,
  MatchedCatalogue,
} from "../models/types";

interface MatchedCatalogueDetailsProps {
  matchedCatalogue?: MatchedCatalogue | null;
  equipmentKind: EquipmentKind;
}

const formatMm = (value: unknown) => {
  if (value == null || value === "") return "—";
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? `${n} mm` : String(value);
};

const MatchPill = ({ matched }: { matched?: boolean }) => (
  <span
    className={`op-match-pill${matched ? " op-match-pill--matched" : " op-match-pill--other"}`}
  >
    {matched ? "Matched" : "Not matched"}
  </span>
);

const SelectedSummary = ({
  title,
  rows,
}: {
  title: string;
  rows: Array<[string, string]>;
}) => (
  <div className="op-matched-selected">
    <h5>{title}</h5>
    <dl className="op-summary-grid">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  </div>
);

function CatalogueMatchTable<T extends { matched?: boolean }>({
  title,
  columns,
  rows,
}: {
  title: string;
  columns: Array<{ key: string; label: string; render?: (row: T) => string }>;
  rows: T[];
}) {
  if (!rows.length) {
    return (
      <div className="op-matched-table-block">
        <h5>{title}</h5>
        <p className="op-muted">No catalogue rows returned.</p>
      </div>
    );
  }

  const sorted = [...rows].sort((a, b) => Number(Boolean(b.matched)) - Number(Boolean(a.matched)));

  return (
    <div className="op-matched-table-block">
      <h5>{title}</h5>
      <div className="op-form-card op-form-card--flush">
        <table className="op-simple-table op-matched-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key}>{col.label}</th>
              ))}
              <th>Match</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, index) => {
              const values = row as unknown as Record<string, unknown>;
              return (
                <tr
                  key={`${title}-${index}`}
                  className={row.matched ? "op-matched-table__row--matched" : undefined}
                >
                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.render ? col.render(row) : String(values[col.key] ?? "—")}
                    </td>
                  ))}
                  <td>
                    <MatchPill matched={Boolean(row.matched)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const BlastFrameMatchedDetails = ({ data }: { data: BlastFrameMatchedCatalogue }) => {
  const selected = data.selected;
  const selectedRows: Array<[string, string]> = [
    [
      "Frame size",
      selected?.frameSize
        ? `${selected.frameSize.label} (index ${selected.frameSize.index})`
        : "—",
    ],
    [
      "Version",
      selected?.version ? `${selected.version.id} (index ${selected.version.index})` : "—",
    ],
    ["Heads", selected?.heads || "—"],
    ["Kite", selected?.kite?.id || "—"],
    [
      "Carriage row",
      selected?.carriageRow
        ? `${selected.carriageRow.frameSize} · ${formatMm(selected.carriageRow.outerBlastCarriageHolePcdMm)}`
        : "—",
    ],
  ];

  return (
    <div className="op-matched-catalogue">
      <div className="op-matched-catalogue__meta">
        {data.ruleVersion ? <span>Rule {data.ruleVersion}</span> : null}
        {data.datasetRevision ? <span>Dataset {data.datasetRevision}</span> : null}
      </div>
      <SelectedSummary title="Selected catalogue row" rows={selectedRows} />
      <CatalogueMatchTable
        title="Frame sizes"
        rows={data.frameSizes || []}
        columns={[
          { key: "label", label: "Label" },
          { key: "index", label: "Index" },
          { key: "minMm", label: "Min", render: (row) => formatMm(row.minMm) },
          { key: "maxMm", label: "Max", render: (row) => formatMm(row.maxMm) },
        ]}
      />
      <CatalogueMatchTable
        title="Length versions"
        rows={data.versions || []}
        columns={[
          { key: "id", label: "Version" },
          { key: "index", label: "Index" },
          {
            key: "innerWheelLengthMm",
            label: "Inner wheel",
            render: (row) => formatMm(row.innerWheelLengthMm),
          },
          {
            key: "overallLengthMm",
            label: "Overall",
            render: (row) => formatMm(row.overallLengthMm),
          },
          {
            key: "carriageLengthMm",
            label: "Carriage",
            render: (row) => formatMm(row.carriageLengthMm),
          },
        ]}
      />
      <CatalogueMatchTable
        title="Kites"
        rows={data.kites || []}
        columns={[
          { key: "id", label: "Kite type" },
          { key: "heads", label: "Heads" },
        ]}
      />
      <CatalogueMatchTable
        title="Carriage rows"
        rows={data.carriageRows || []}
        columns={[
          { key: "frameSize", label: "Frame size" },
          {
            key: "outerBlastCarriageHolePcdMm",
            label: "Outer blast PCD",
            render: (row) => formatMm(row.outerBlastCarriageHolePcdMm),
          },
        ]}
      />
    </div>
  );
};

const InductionCoilMatchedDetails = ({ data }: { data: InductionCoilMatchedCatalogue }) => {
  const selected = data.selected;
  const selectedRows: Array<[string, string]> = [
    [
      "Frame",
      selected?.frame
        ? `${selected.frame.nbIn}" ${selected.frame.clearanceType} (index ${selected.frame.index})`
        : "—",
    ],
    [
      "Version",
      selected?.version ? `${selected.version.id} (index ${selected.version.index})` : "—",
    ],
    ["CWC hardware", selected?.cwcHardware || "—"],
  ];

  return (
    <div className="op-matched-catalogue">
      <div className="op-matched-catalogue__meta">
        {data.ruleVersion ? <span>Rule {data.ruleVersion}</span> : null}
        {data.datasetRevision ? <span>Dataset {data.datasetRevision}</span> : null}
      </div>
      <SelectedSummary title="Selected catalogue row" rows={selectedRows} />
      <CatalogueMatchTable
        title="Frames"
        rows={data.frames || []}
        columns={[
          { key: "nbIn", label: "NB (in)" },
          { key: "index", label: "Index" },
          { key: "clearanceType", label: "Clearance" },
          { key: "idMm", label: "ID", render: (row) => formatMm(row.idMm) },
        ]}
      />
      <CatalogueMatchTable
        title="Bottom-end versions"
        rows={data.versions || []}
        columns={[
          { key: "id", label: "Version" },
          { key: "index", label: "Index" },
          {
            key: "heatLengthMm",
            label: "Heat length",
            render: (row) => formatMm(row.heatLengthMm),
          },
          {
            key: "bottomEndLengthMm",
            label: "Bottom end",
            render: (row) => formatMm(row.bottomEndLengthMm),
          },
        ]}
      />
    </div>
  );
};

const MatchedCatalogueDetails = ({
  matchedCatalogue,
  equipmentKind,
}: MatchedCatalogueDetailsProps) => {
  if (!matchedCatalogue) {
    return (
      <p className="op-muted op-matched-empty">
        No matched catalogue data was returned for this configuration.
      </p>
    );
  }

  if (equipmentKind === "blast-frame") {
    if (!matchedCatalogue.blastFrame) {
      return (
        <p className="op-muted op-matched-empty">
          No Blast Frame matched-catalogue data was returned.
        </p>
      );
    }
    return <BlastFrameMatchedDetails data={matchedCatalogue.blastFrame} />;
  }

  if (!matchedCatalogue.inductionCoil) {
    return (
      <p className="op-muted op-matched-empty">
        No Induction Coil matched-catalogue data was returned.
      </p>
    );
  }
  return <InductionCoilMatchedDetails data={matchedCatalogue.inductionCoil} />;
};

export default MatchedCatalogueDetails;
